import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Save, X, User, Upload, FileText, Trash2, CreditCard } from 'lucide-react';
import { TeacherFormData } from '../../types';
import * as yup from 'yup';

interface TeacherFormProps {
  onSubmit: (data: TeacherFormData) => void;
  loading?: boolean;
  onCancel: () => void;
  initialData?: Partial<TeacherFormData>;
}

const teacherSchema = yup.object({
  name: yup
    .string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters'),
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(/^[0-9]{10}$/, 'Phone number must be 10 digits'),
  aadharNumber: yup
    .string()
    .required('Aadhar number is required')
    .matches(/^[0-9]{12}$/, 'Aadhar number must be 12 digits'),
  subject: yup
    .string()
    .required('Subject is required'),
  qualification: yup
    .string()
    .required('Qualification is required'),
  dateOfJoining: yup
    .string()
    .required('Date of joining is required'),
  salary: yup
    .number()
    .required('Salary is required')
    .positive('Salary must be positive')
    .min(1000, 'Salary must be at least ₹1,000'),
  photo: yup
    .mixed()
    .optional(),
  documents: yup
    .mixed()
    .optional(),
  bankDetails: yup
    .object({
      accountHolderName: yup.string().required('Account holder name is required'),
      accountNumber: yup
        .string()
        .required('Account number is required')
        .matches(/^[0-9]{9,18}$/, 'Account number must be 9-18 digits'),
      bankName: yup.string().required('Bank name is required'),
      branchName: yup.string().required('Branch name is required'),
      ifscCode: yup
        .string()
        .required('IFSC code is required')
        .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC code format'),
      accountType: yup
        .string()
        .required('Account type is required')
        .oneOf(['Savings', 'Current'], 'Please select a valid account type')
    })
    .optional()
});

const TeacherForm: React.FC<TeacherFormProps> = ({
  onSubmit,
  loading = false,
  onCancel,
  initialData
}) => {
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<File[]>([]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(teacherSchema),
    defaultValues: {
      name: initialData?.name || '',
      email: initialData?.email || '',
      phone: initialData?.phone || '',
      aadharNumber: initialData?.aadharNumber || '',
      subject: initialData?.subject || '',
      qualification: initialData?.qualification || '',
      dateOfJoining: initialData?.dateOfJoining || '',
      salary: initialData?.salary || 0,
      bankDetails: initialData?.bankDetails || {
        accountHolderName: '',
        accountNumber: '',
        bankName: '',
        branchName: '',
        ifscCode: '',
        accountType: 'Savings'
      }
    }
  });

  const handleFormSubmit = (data: any) => {
    const formData = {
      ...data,
      documents: selectedDocuments.length > 0 ? selectedDocuments : undefined
    };
    onSubmit(formData as TeacherFormData);
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setValue('photo', file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDocumentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setSelectedDocuments(prev => [...prev, ...newFiles]);
    }
    // Reset the input so the same file can be selected again
    event.target.value = '';
  };

  const removeDocument = (index: number) => {
    setSelectedDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const subjects = [
    'Mathematics',
    'Science',
    'English',
    'Hindi',
    'Social Studies',
    'Physics',
    'Chemistry',
    'Biology',
    'Computer Science',
    'Physical Education',
    'Art',
    'Music',
    'Sanskrit',
    'Geography',
    'History',
    'Economics',
    'Accountancy',
    'Business Studies'
  ];

  return (
    <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6 space-y-6">
        {/* Personal Information */}
        <div>
          <h3 className="text-lg font-medium text-[#f3ede2] mb-4">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Full Name *
              </label>
              <input
                type="text"
                {...register('name')}
                placeholder="Enter teacher's full name"
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              />
              {errors.name && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Email Address *
              </label>
              <input
                type="email"
                {...register('email')}
                placeholder="teacher@school.edu"
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              />
              {errors.email && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                {...register('phone')}
                placeholder="9876543210"
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              />
              {errors.phone && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.phone.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Aadhar Number *
              </label>
              <input
                type="text"
                {...register('aadharNumber')}
                placeholder="123456789012"
                maxLength={12}
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              />
              {errors.aadharNumber && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.aadharNumber.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Date of Joining *
              </label>
              <input
                type="date"
                {...register('dateOfJoining')}
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              />
              {errors.dateOfJoining && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.dateOfJoining.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Academic Information */}
        <div>
          <h3 className="text-lg font-medium text-[#f3ede2] mb-4">Academic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Subject *
              </label>
              <select
                {...register('subject')}
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              >
                <option value="">Select Subject</option>
                {subjects.map((subject) => (
                  <option key={subject} value={subject}>
                    {subject}
                  </option>
                ))}
              </select>
              {errors.subject && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.subject.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Qualification *
              </label>
              <input
                type="text"
                {...register('qualification')}
                placeholder="e.g., M.A., B.Ed., Ph.D."
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              />
              {errors.qualification && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.qualification.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Monthly Salary (₹) *
              </label>
              <input
                type="number"
                {...register('salary', { valueAsNumber: true })}
                placeholder="50000"
                min="0"
                step="100"
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              />
              {errors.salary && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.salary.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Photo Upload */}
        <div>
          <h3 className="text-lg font-medium text-[#f3ede2] mb-4">Profile Photo</h3>
          <div className="flex items-start space-x-6">
            <div className="flex-shrink-0">
              <div className="w-24 h-24 bg-[#211c15] rounded-lg flex items-center justify-center border-2 border-dashed border-[#3a3122]">
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <User className="w-8 h-8 text-[#8a7f6e]" />
                )}
              </div>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Upload Photo
              </label>
              <div className="flex items-center space-x-3">
                <label className="flex items-center space-x-2 px-4 py-2 bg-[#211c15] hover:bg-[#28221a] border border-[#3a3122] rounded-lg cursor-pointer transition-colors">
                  <Upload className="w-4 h-4" />
                  <span className="text-sm">Choose File</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] hidden"
                  />
                </label>
                <span className="text-sm text-[#8a7f6e]">Optional: JPG, PNG up to 5MB</span>
              </div>
            </div>
          </div>
        </div>

        {/* Documents */}
        <div>
          <h3 className="text-lg font-medium text-[#f3ede2] mb-4">Documents</h3>

          {/* Document Upload */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
              Attach Documents
            </label>
            <div className="flex items-center space-x-3">
              <label className="flex items-center space-x-2 px-4 py-2 bg-[#211c15] hover:bg-[#28221a] border border-[#3a3122] rounded-lg cursor-pointer transition-colors">
                <Upload className="w-4 h-4" />
                <span className="text-sm">Choose Files</span>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleDocumentChange}
                  className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] hidden"
                />
              </label>
              <span className="text-sm text-[#8a7f6e]">
                PDF, DOC, DOCX, JPG, PNG (Max 10MB each)
              </span>
            </div>
          </div>

          {/* Selected Documents List */}
          {selectedDocuments.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-[#b9ad9b] mb-3">
                Selected Documents ({selectedDocuments.length})
              </h4>
              <div className="space-y-2">
                {selectedDocuments.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-[#28221a] border border-[#3a3122] rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-4 h-4 text-[#8a7f6e]" />
                      <div>
                        <div className="text-sm font-medium text-[#f3ede2]">{file.name}</div>
                        <div className="text-xs text-[#8a7f6e]">{formatFileSize(file.size)}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDocument(index)}
                      className="p-1 text-[#f38b83] hover:bg-[#341a17] rounded transition-colors"
                      title="Remove document"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-3 p-3 bg-[#16212b] border border-[#2a3d4d] rounded-lg">
            <h4 className="text-sm font-medium text-[#bfdbfe] mb-2">Suggested Documents:</h4>
            <div className="text-sm text-[#93c5fd] space-y-1">
              <p>• Resume/CV</p>
              <p>• Educational Certificates</p>
              <p>• Experience Letters</p>
              <p>• Aadhar Card Copy</p>
              <p>• PAN Card Copy</p>
              <p>• Bank Account Details</p>
              <p>• Police Verification Certificate</p>
            </div>
          </div>
        </div>

        {/* Bank Details */}
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <CreditCard className="w-5 h-5 text-[#b9ad9b]" />
            <h3 className="text-lg font-medium text-[#f3ede2]">Bank Details</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Account Holder Name *
              </label>
              <input
                type="text"
                {...register('bankDetails.accountHolderName')}
                placeholder="Enter account holder name"
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              />
              {errors.bankDetails?.accountHolderName && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.bankDetails.accountHolderName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Account Number *
              </label>
              <input
                type="text"
                {...register('bankDetails.accountNumber')}
                placeholder="Enter account number"
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              />
              {errors.bankDetails?.accountNumber && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.bankDetails.accountNumber.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Bank Name *
              </label>
              <input
                type="text"
                {...register('bankDetails.bankName')}
                placeholder="e.g., State Bank of India"
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              />
              {errors.bankDetails?.bankName && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.bankDetails.bankName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Branch Name *
              </label>
              <input
                type="text"
                {...register('bankDetails.branchName')}
                placeholder="Enter branch name"
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              />
              {errors.bankDetails?.branchName && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.bankDetails.branchName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                IFSC Code *
              </label>
              <input
                type="text"
                {...register('bankDetails.ifscCode')}
                placeholder="e.g., SBIN0001234"
                maxLength={11}
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent uppercase"
              />
              {errors.bankDetails?.ifscCode && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.bankDetails.ifscCode.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Account Type *
              </label>
              <select
                {...register('bankDetails.accountType')}
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              >
                <option value="Savings">Savings Account</option>
                <option value="Current">Current Account</option>
              </select>
              {errors.bankDetails?.accountType && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.bankDetails.accountType.message}</p>
              )}
            </div>
          </div>

          <div className="mt-4 p-3 bg-info-50 border border-info-200 rounded-lg">
            <h4 className="text-sm font-medium text-info-800 mb-2">Important Note:</h4>
            <p className="text-sm text-info-700">
              Bank details are required for salary payments. Please ensure all information is accurate as per your bank account details.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 pt-6 border-t border-[#3a3122]">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-[#b9ad9b] border border-[#3a3122] rounded-lg hover:bg-[#28221a] transition-colors"
          >
            <X className="w-4 h-4 inline-block mr-2" />
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] disabled:bg-[#8a7f6e] disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            <Save className="w-4 h-4" />
            <span>{loading ? 'Saving...' : initialData ? 'Update' : 'Add'} Teacher</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeacherForm;